<template>
  <div class="ticket-attachments">
    <!-- Header -->
    <div class="flex gap-3 md:gap-6 justify-between">
      <div v-if="!hiddenTitle" class="flex flex-col title-wrapper">
        <h3 v-if="title" class="mb-2 font-bold text-lg">{{ title }}</h3>
        <span v-if="subtitle" class="text-xs">{{ subtitle }}</span>
      </div>

      <h5 v-if="multiple" class="mt-3 font-bold whitespace-no-wrap">
        {{ attachments.length + " / " + maxAttachments }}
      </h5>
    </div>

    <!-- Attachments -->
    <div
      style="overflow-x: auto"
      :class="hiddenTitle ? 'mt-0' : 'mt-4'"
      class="mb-0 flex gap-3"
    >
      <div
        v-for="attachment in attachments"
        :key="attachment.id"
        style="margin-bottom: 10px; width: 112px"
        class="flex items-center h-full"
      >
        <div
          v-on:click="(event) => handleClickFile(event, attachment)"
          class="flex-1 relative"
        >
          <div class="show-image relative" v-if="attachment.url">
            <img
              class="rounded overflow-hidden object-contain"
              style="width: 100%; height: 100%"
              :src="attachment.url"
            />
          </div>

          <div style="top: 4px; right: 4px" class="flex gap-1 absolute">
            <vs-button
              v-if="attachment.url"
              v-on:click="(event) => toggleImagePreview(event, attachment.url)"
              color="primary"
              type="filled"
              size="small"
              icon-pack="feather"
              icon="icon-eye"
            />
            <vs-button
              v-on:click="(event) => deleteAttachment(event, attachment)"
              color="danger"
              type="filled"
              size="small"
              icon-pack="feather"
              icon="icon-trash-2"
            />
          </div>
        </div>
      </div>

      <div
        v-if="
          (multiple || attachments.length < 1) &&
          attachments.length < maxAttachments
        "
        :style="`margin-bottom: 10px; width:${
          multiple && attachments.length > 0 ? '112px' : '100%'
        };`"
        class="flex items-center"
        @click="triggerAdd"
      >
        <div class="upload-box">
          <div class="placeholder">
            <i class="feather icon-upload"></i>
            <p>
              افزودن
              {{ accept.includes("image") ? "تصویر" : "فایل" }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <input
      ref="addInput"
      type="file"
      :accept="accept"
      style="display: none"
      @change="handleSelectFile"
    />

    <!-- Crop popup -->
    <vs-popup title="برش عکس" :active.sync="cropPopupVisible">
      <cropper
        :imageBase64="imageBase64"
        :cropPopupVisible="cropPopupVisible"
        :aspectRatio="aspectRatio"
        :fileType="fileType"
        @cropImage="handleCropConfirm"
      />
    </vs-popup>

    <image-preview ref="imagePreviewRef" :image="selectedImageForPreview" />
  </div>
</template>

<script>
  import { mapGetters } from "vuex";
  import Cropper from "../../../components/image/Cropper.vue";
  import ImagePreview from "../../../components/image/ImagePreview";
  import providerApiService from "../../../services/api/ProviderApiService";
  import heic2any from "heic2any";
  import {
    convertBlobToFile,
    generateRandomNumber,
  } from "../../../helpers/Functions";

  export default {
    name: "Attachments",
    components: { Cropper, ImagePreview },

    props: {
      data: Array,
      aspectRatio: Number,
      title: String,
      subtitle: String,
      multiple: { type: Boolean, default: true },
      hiddenTitle: { type: Boolean, default: false },
      accept: { type: String, default: "file/*" },
      urlForDelete: String,
    },

    data() {
      return {
        attachments: this.initAttachments(),
        maxAttachments: 10,

        pendingFile: null,
        imageBase64: "",
        fileType: "",
        cropPopupVisible: false,

        selectedImageForPreview: "",
      };
    },

    computed: {
      ...mapGetters({
        currentProvider: "profile/currentProvider",
      }),
    },

    methods: {
      initAttachments() {
        if (Array.isArray(this.data)) {
          return this.data.map((item) => ({
            id: item.id,
            url: item.url,
            isFromServer: true,
          }));
        }
        return [];
      },

      triggerAdd() {
        this.$refs.addInput.click();
      },

      async handleSelectFile(event) {
        const file = event.target.files[0];
        if (!file) return;

        const ext = file.name.split(".").pop().toLowerCase();

        if (ext === "heic") {
          await heic2any({
            blob: file,
            toType: "image/jpeg",
            quality: 0.5,
          })
            .then((converted) => {
              this.prepareCrop(converted);
            })
            .catch(() => {
              this.$vs.notify({
                text: "خطا در بارگذاری فایل. لطفا مجدد تلاش کنید.",
                color: "danger",
                position: "bottom-center",
              });
            });
        } else if (file.type.includes("image")) {
          this.prepareCrop(file);
        } else {
          this.addFinalAttachment(file);
        }

        event.target.value = "";
      },

      prepareCrop(file) {
        this.fileType = file.type;
        this.pendingFile = file;
        this.imageBase64 = URL.createObjectURL(file);
        this.cropPopupVisible = true;
      },

      handleCropConfirm(blob) {
        this.cropPopupVisible = false;
        const finalFile = convertBlobToFile(blob);
        this.addFinalAttachment(finalFile);

        this.pendingFile = null;
        this.imageBase64 = "";
      },

      addFinalAttachment(file) {
        this.attachments.push({
          id: generateRandomNumber(),
          file,
          url: URL.createObjectURL(file),
          isFromServer: false,
        });
      },

      deleteAttachment(event, attachment) {
        event.stopPropagation();
        if (attachment.isFromServer) {
          this.$vs.dialog({
            type: "confirm",
            color: "danger",
            title: `حذف عکس`,
            text: `از حذف عکس اطمینان دارید؟`,
            acceptText: "بلی",
            cancelText: "خیر",
            accept: () => this.deleteFromServer(attachment),
          });
        } else {
          this.attachments = this.attachments.filter(
            (i) => i.id !== attachment.id
          );
        }
      },

      deleteFromServer(attachment) {
        this.$vs.loading();

        providerApiService
          .deleteAttachment(
            this.urlForDelete,
            this.currentProvider.id,
            attachment.id
          )
          .then(() => {
            this.attachments = this.attachments.filter(
              (i) => i.id !== attachment.id
            );
            this.$vs.notify({
              text: "عملیات با موفقیت انجام شد.",
              color: "success",
              position: "bottom-center",
            });
          })
          .catch(() =>
            this.$vs.notify({
              text: "عملیات با خطا مواجه شد.",
              color: "danger",
              position: "bottom-center",
            })
          )
          .finally(() => this.$vs.loading.close());
      },

      toggleImagePreview(event, url) {
        event.stopPropagation();
        this.selectedImageForPreview = url;
        this.$refs.imagePreviewRef.togglePreviewVisibility();
      },

      getData() {
        return this.attachments.filter((i) => i.file).map((i) => i.file);
      },
      handleClickFile(event, attachment) {
        if (attachment.isFromServer) {
          event.preventDefault();
          this.$vs.notify({
            text: "برای ویرایش عکس ابتدا آن را حذف کنید. سپس عکس مورد نظر خود را انتخاب کنید.",
            color: "warning",
            position: "bottom-center",
          });
        }
      },
    },
  };
</script>

<style lang="scss">
  .ticket-attachments {
    flex: 1;

    .title-wrapper {
      max-width: 380px;
    }

    @media (max-width: 768px) {
      .title-wrapper {
        max-width: 300px;
      }
    }

    .show-image {
      height: 112px;
      min-width: 112px;
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .upload-box {
      border: 1px dashed #cfcfcf;
      border-radius: 10px;
      height: 112px;
      min-width: 112px;
      width: 100%;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fafafa;

      &:hover {
        border-color: rgb(var(--vs-primary));
      }

      .placeholder {
        text-align: center;

        i {
          font-size: 32px;
          margin-bottom: 8px;
        }
      }
    }
  }
</style>
